import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ListFilter, Plus, Pin, Pencil, Trash2 } from "lucide-react";
import { Badge, BadgeColorPicker, Button, EmptyState, Skeleton, Modal, ModalFooter, Input, Label, useConfirm, useToast } from "@/components/ui";
import { api, ApiError } from "@/lib/api";
import { SmartFilterBuilder, type FilterSpec } from "@/features/lists/SmartFilterBuilder";
import type { ContactList } from "@/lib/types";

export function ListsPage() {
  const toast = useToast();
  const confirm = useConfirm();
  const [lists, setLists] = useState<ContactList[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<ContactList | "new" | null>(null);

  const load = async () => {
    setLoading(true);
    try {
      const d = await api.get<{ lists: ContactList[] }>("/lists");
      setLists(d.lists);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Failed");
    } finally { setLoading(false); }
  };
  useEffect(() => { void load(); }, []);

  const onDelete = async (l: ContactList) => {
    if (!(await confirm({
      title: `Delete "${l.name}"?`,
      description: "This list will be removed. The contacts in it are not deleted.",
      confirmLabel: "Delete list",
      danger: true,
    }))) return;
    setLists((ls) => ls.filter((x) => x.id !== l.id));
    try {
      await api.delete(`/lists/${l.id}`);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Failed");
      load();
    }
  };
  const togglePin = async (l: ContactList) => {
    setLists((ls) => ls.map((x) => x.id === l.id ? { ...x, pinned: !x.pinned } : x));
    try {
      await api.patch(`/lists/${l.id}`, { pinned: !l.pinned });
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Failed");
      load();
    }
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-zinc-100 sm:text-2xl">Smart Lists</h1>
          <p className="mt-1 text-sm text-zinc-500">Saved filters that always show the latest contacts matching your conditions.</p>
        </div>
        <Button leftIcon={<Plus className="h-4 w-4" />} onClick={() => setEditing("new")}>New smart list</Button>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-28" />)}
        </div>
      ) : lists.length === 0 ? (
        <EmptyState
          icon={ListFilter}
          title="No smart lists yet"
          description="A smart list is a saved filter — like 'all customers in California' or 'leads with no activity in 30 days'. They update themselves whenever your data changes."
          action={<Button leftIcon={<Plus className="h-4 w-4" />} onClick={() => setEditing("new")}>Create your first smart list</Button>}
        />
      ) : (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {lists.map((l) => (
            <div
              key={l.id}
              className="group flex flex-col rounded-xl border border-zinc-800/70 bg-zinc-900/30 p-4 transition-colors hover:border-zinc-700"
            >
              <div className="flex items-start justify-between gap-2">
                <div className="flex items-center gap-2 min-w-0">
                  <Badge color={l.color}>{l.name}</Badge>
                  <button onClick={() => togglePin(l)} aria-label="Pin" title={l.pinned ? "Unpin" : "Pin"}>
                    <Pin className={`h-3.5 w-3.5 ${l.pinned ? "text-amber-400" : "text-zinc-600 hover:text-zinc-300"}`} />
                  </button>
                </div>
                <span className="text-xs text-zinc-500 tabular-nums">{l.count.toLocaleString()}</span>
              </div>
              {l.description && <p className="mt-2 text-xs text-zinc-500 line-clamp-2">{l.description}</p>}
              <p className="mt-2 text-[10px] text-zinc-600">
                {l.filter.rules && l.filter.rules.length > 0
                  ? `${l.filter.rules.length} ${l.filter.rules.length === 1 ? "rule" : "rules"} · ${l.filter.match === "any" ? "any match" : "all match"}`
                  : "No conditions — all contacts"}
              </p>
              <div className="mt-3 flex items-center gap-1 opacity-70 group-hover:opacity-100 transition-opacity">
                <Link to={`/contacts?list=${l.id}`} className="flex-1">
                  <Button variant="outline" size="sm" className="w-full">Open</Button>
                </Link>
                <Button variant="ghost" size="icon" onClick={() => setEditing(l)} aria-label="Edit"><Pencil className="h-3.5 w-3.5" /></Button>
                <Button variant="ghost" size="icon" onClick={() => onDelete(l)} aria-label="Delete"><Trash2 className="h-3.5 w-3.5" /></Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <ListEditDialog
          list={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function ListEditDialog({ list, onClose, onSaved }: {
  list: ContactList | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const toast = useToast();
  const [name, setName] = useState(list?.name ?? "");
  const [description, setDescription] = useState(list?.description ?? "");
  const [color, setColor] = useState(list?.color ?? "indigo");
  const [filter, setFilter] = useState<FilterSpec>(list?.filter ?? { match: "all", rules: [] });
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!name.trim()) return;
    setSaving(true);
    try {
      const body = { name: name.trim(), description: description.trim() || null, color, filter };
      if (list) await api.patch(`/lists/${list.id}`, body);
      else      await api.post("/lists", body);
      toast.success(list ? "Updated" : "Created");
      onSaved();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Failed");
    } finally { setSaving(false); }
  };

  return (
    <Modal open onClose={onClose} title={list ? "Edit smart list" : "New smart list"} size="lg">
      <div className="space-y-4">
        <div className="space-y-4">
          <div>
            <Label required>Name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Customers in California" autoFocus />
          </div>
          <div>
            <Label>Color</Label>
            <BadgeColorPicker value={color} onChange={setColor} />
          </div>
        </div>
        <div>
          <Label>Description</Label>
          <Input value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Optional" />
        </div>
        <div>
          <Label>Conditions</Label>
          <SmartFilterBuilder filter={filter} onChange={setFilter} />
        </div>
      </div>
      <ModalFooter>
        <Button variant="ghost" onClick={onClose}>Cancel</Button>
        <Button onClick={submit} loading={saving} disabled={!name.trim()}>{list ? "Save changes" : "Create"}</Button>
      </ModalFooter>
    </Modal>
  );
}
